import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBookOpen, FiCheckCircle, FiCircle, FiClock, FiAward, FiLock } from 'react-icons/fi';
import { getTrainingModule, requestLevelExam } from '../services/api';
import { CAREER_TRACKS } from '../components/CareerBadge';

function trackStyle(track) {
  if (track === 'Licensure Prep') return { color: '#dc2626', bg: '#fee2e2' };
  return CAREER_TRACKS[track] || { color: '#475569', bg: '#f1f5f9' };
}

export default function TrainingModule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getTrainingModule(id)
      .then(res => {
        setModule(res.data);
        setError('');
      })
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load module.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleRequestExam = async () => {
    if (!window.confirm(
      `You're about to request the proctored level exam for "${module.title}".\n\n` +
      `An admin will need to physically issue you a 6-digit unlock code.\n` +
      `You'll have ${module.exam_time_minutes} minutes for ~${module.exam_question_count} questions, ` +
      `passing at ${module.pass_threshold}%.\n\nContinue?`
    )) return;
    setRequesting(true);
    try {
      await requestLevelExam(module.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not submit exam request.');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) return <div style={{ padding: 28 }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 28 }}>
      <Link to="/training" style={{ color: '#1e40af' }}>← Training</Link>
      <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginTop: 12 }}>{error}</div>
    </div>
  );
  if (!module) return null;

  const style = trackStyle(module.track);
  const lessons = module.lessons || [];
  const completedCount = lessons.filter(l => l.completed).length;
  const allComplete = module.all_lessons_complete;
  const passed = module.has_passed;
  const pendingUnlock = module.pending_unlock;
  const latestAttempt = module.latest_attempt;

  const objectives = (module.objectives || '').split('\n').map(o => o.trim()).filter(Boolean);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1000, margin: '0 auto' }}>
      <Link to="/training" style={{ color: '#1e40af', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, textDecoration: 'none' }}>
        <FiArrowLeft size={14} /> All Training
      </Link>

      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <span
          style={{
            background: style.bg,
            color: style.color,
            fontSize: 11,
            padding: '4px 12px',
            borderRadius: 12,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase'
          }}
        >
          {module.track} — Level {module.level}
        </span>
        <h1 style={{ margin: '10px 0 8px 0', color: '#0f172a' }}>{module.title}</h1>
        <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{module.summary}</p>
      </div>

      {/* Progress + exam status row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          marginBottom: 20
        }}
      >
        <Stat label="Lessons" value={`${completedCount}/${lessons.length}`} accent={style.color} />
        <Stat
          label="Practice progress"
          value={`${lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0}%`}
          accent={style.color}
        />
        <Stat
          label="Level exam"
          value={passed ? `Passed (${latestAttempt?.score || 0}%)` : (latestAttempt ? `Attempted (${latestAttempt.score || 0}%)` : 'Not attempted')}
          accent={passed ? '#16a34a' : '#64748b'}
        />
      </div>

      {/* Exam status banner */}
      {passed ? (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#14532d', padding: 16, borderRadius: 10, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAward size={22} />
          <div>
            <strong>You've passed this level.</strong>
            {latestAttempt?.advancement_approved_at
              ? ' Advancement was approved by an admin.'
              : ' Advancement is awaiting admin approval.'}
          </div>
        </div>
      ) : pendingUnlock ? (
        <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e3a8a', padding: 16, borderRadius: 10, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiClock size={22} />
          <div>
            {pendingUnlock.status === 'unlocked'
              ? <><strong>Exam unlocked.</strong> Ask your proctor for the 6-digit code; enter it on the Training home page to start. Code expires {new Date(pendingUnlock.expires_at + 'Z').toLocaleString()}.</>
              : <><strong>Exam request submitted.</strong> An admin has been notified. They will issue your unlock code in person when ready to proctor.</>
            }
          </div>
        </div>
      ) : allComplete ? (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', padding: 16, borderRadius: 10, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <FiLock color="#c2410c" size={22} />
            <strong style={{ color: '#9a3412' }}>Ready for the level exam</strong>
          </div>
          <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#9a3412' }}>
            All lessons complete. The proctored level exam is {module.exam_time_minutes} minutes,
            ~{module.exam_question_count} questions, {module.pass_threshold}% to pass. An admin
            will need to be physically present to issue your unlock code.
          </p>
          <button
            disabled={requesting}
            onClick={handleRequestExam}
            style={{
              background: '#c2410c',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            {requesting ? 'Requesting…' : 'Request level exam'}
          </button>
        </div>
      ) : (
        <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: 14, borderRadius: 10, marginBottom: 24, fontSize: 14, color: '#475569' }}>
          Complete all lessons to unlock the level exam request.
        </div>
      )}

      {objectives.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 15, color: '#0f172a' }}>What you'll learn</h3>
          <ul style={{ margin: 0, paddingLeft: 22, color: '#334155', fontSize: 14, lineHeight: 1.6 }}>
            {objectives.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </section>
      )}

      <h3 style={{ margin: '0 0 12px 0', fontSize: 15, color: '#0f172a' }}>Lessons</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {lessons.map((l, idx) => (
          <button
            key={l.id}
            onClick={() => navigate(`/training/lessons/${l.id}`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              border: `1px solid ${l.completed ? '#bbf7d0' : '#e2e8f0'}`,
              borderRadius: 8,
              background: l.completed ? '#f0fdf4' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.1s, border 0.1s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = l.completed ? '#dcfce7' : '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = l.completed ? '#f0fdf4' : 'white'}
          >
            <div style={{ width: 28, color: l.completed ? '#16a34a' : '#94a3b8', flexShrink: 0 }}>
              {l.completed ? <FiCheckCircle size={20} /> : <FiCircle size={20} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                {idx + 1}. {l.title}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {l.estimated_minutes} min
                {l.practice_question_count > 0 && ` · ${l.practice_question_count} practice questions`}
                {l.best_practice_score !== null && l.best_practice_score !== undefined && ` · best score: ${l.best_practice_score}%`}
              </div>
            </div>
            <FiBookOpen color="#64748b" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, color: accent, fontWeight: 700, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}
